import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { PlaylistCriteriaDirective } from '@app/library/components/sides/playlist-criteria/playlist-criteria.directive';
import { Criterion, operators, textInput } from '@app/library/models/criterias.model';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { FormHelperService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';

const textOperators = [
  {
    id: operators.EQUALS,
    name: operators.EQUALS,
    translation: marker('PLAYLIST.CRITERIA.EQUALS'),
  },
  {
    id: operators.CONTAINS,
    name: operators.CONTAINS,
    translation: marker('PLAYLIST.CRITERIA.CONTAINS'),
  },
  {
    id: operators.NOT_EQUALS,
    name: operators.NOT_EQUALS,
    translation: marker('PLAYLIST.CRITERIA.NOT_EQUALS'),
  },
  {
    id: operators.NOT_CONTAINS,
    name: operators.NOT_CONTAINS,
    translation: marker('PLAYLIST.CRITERIA.NOT_CONTAINS'),
  },
];

@Component({
  selector: 'rk-playlist-criteria-text',
  templateUrl: './playlist-criteria-text.component.html',
  styleUrls: ['./playlist-criteria-text.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PlaylistCriteriaTextComponent),
      multi: true,
    },
  ],
})
export class PlaylistCriteriaTextComponent
  extends PlaylistCriteriaDirective
  implements OnInit, ControlValueAccessor
{
  @Input()
  data: SugestionInput[];

  constructor(private readonly fb: UntypedFormBuilder, formHelper: FormHelperService) {
    super(formHelper);
  }

  ngOnInit() {
    super.ngOnInit();
    this.criteriaForm = this.fb.group({
      field: '',
      operator: textOperators[0],
      value: '',
      chipsValue: [[]],
    });
  }

  shouldReset(prevOp: operators, op: operators): boolean {
    if (prevOp === op) {
      return false;
    }
    if (
      (prevOp === operators.EQUALS && op === operators.CONTAINS) ||
      (prevOp === operators.CONTAINS && op === operators.EQUALS)
    ) {
      return false;
    }

    return !(
      (prevOp === operators.NOT_EQUALS && op === operators.NOT_CONTAINS) ||
      (prevOp === operators.NOT_CONTAINS && op === operators.NOT_EQUALS)
    );
  }

  formValueToCriteria(formData: any): Criterion {
    const shouldReset = this.shouldReset(formData.operator.id, this.prevOperator['id']);
    this.prevOperator = formData.operator;
    const firstText = this.data ? this.data[0] : null;
    if (shouldReset) {
      this.criteriaForm.clearValidators();
      this.criteriaForm.get('value').setValue(firstText);
      this.criteriaForm.get('chipsValue').setValue([]);
    }

    return {
      field: formData.field,
      operator: formData.operator['id'],
      value: shouldReset
        ? ''
        : this.isTextInput()
        ? formData.value.name
        : formData.chipsValue.map((chip: any) => chip.name),
    };
  }

  criteriaToFormValue(val: any): any {
    const ret = {
      ...val,
      operator: this.findOperatorWithId(val.operator),
      chipsValue: [],
    };
    this.prevOperator = ret.operator;

    if (val.value instanceof Array) {
      ret['chipsValue'] = val.value.map((chip: any, i: any) => ({ id: i, name: chip }));
      ret['value'] = '';
    } else if (val.value) {
      ret['value'] = this.data.find(d => d.name === val.value);
      if (!ret['value']) {
        ret['value'] = { id: 0, name: val.value };
      }
    }

    return ret;
  }

  findOperatorWithId(name: string) {
    return textOperators.find(operator => operator.id === name);
  }

  generateOperatorsInput(): SugestionInput[] {
    return textOperators;
  }

  registerOnChange(fn: any): void {
    this.criteriaForm.valueChanges.subscribe(val => fn(this.formValueToCriteria(val)));
  }

  writeValue(value: any): void {
    if (!value) {
      return;
    }
    this.criteriaForm.setValue(this.criteriaToFormValue(value), { emitEvent: false });
  }

  isTextInput() {
    return this.criteriaForm.get('operator').value
      ? textInput(this.criteriaForm.get('operator').value.name)
      : false;
  }
}
